import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faChartBar } from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { BarLineChartProps, ChartType } from './types';
import {
  ChartCard,
  ChartContainer,
  EmptyContainer,
  EmptyText,
  EmptyIconWrapper,
} from './styles';
import { LABEL_MAPPING, NUMBER_FORMAT_KEYS, DEFAULT_CHART_HEIGHT } from './constants';

const BarLineChart: React.FC<BarLineChartProps> = ({
  data,
  chartType = 'bar',
  xKey,
  yKey,
  valueKey,
  isLoading = false,
  height = DEFAULT_CHART_HEIGHT,
}) => {
  const { isDarkMode } = useTheme();
  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };        
  const normalizedChartType: ChartType = (chartType === 'line' ? 'line' : 'bar') as ChartType;
  const yAxisTickFormatter = useMemo(() => {
    return (value: number) => {
      if (NUMBER_FORMAT_KEYS.includes(yKey)) {
        return `${value.toLocaleString()}`;
      }
      return `$${value.toLocaleString()}`;
    };
  }, [yKey]);

  const tooltipFormatter = useMemo(() => {
    return (value: number | string, name: string) => {
      const formattedName = LABEL_MAPPING[name] || name;
      const formattedValue =
        typeof value === 'number'
          ? NUMBER_FORMAT_KEYS.includes(name)
            ? value.toLocaleString()
            : `$${value.toLocaleString()}`
          : value;
      return [formattedValue, formattedName];
    };
  }, []);

  const chartColors = useMemo(() => {
    return {
      grid: isDarkMode ? '#4a5568' : '#e5e7eb',
      axis: isDarkMode ? '#a0aec0' : '#666',
      bar: '#3182CE',
      line: '#3182CE',
    };
  }, [isDarkMode]);

  const renderChart = () => {
    if (!data || data.length === 0) {
      return (
        <EmptyContainer>
          <EmptyIconWrapper>
            <FontAwesomeIcon icon={normalizedChartType === 'line' ? faChartLine : faChartBar} />
          </EmptyIconWrapper>
          <EmptyText>No data for current selection</EmptyText>
        </EmptyContainer>
      );
    }

    if (normalizedChartType === 'line') {
      return (
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
          <XAxis
            dataKey={xKey}
            stroke={chartColors.axis}
            fontSize={12}
            tick={{ fill: chartColors.axis }}
          />
          <YAxis
            stroke={chartColors.axis}
            fontSize={12}
            tick={{ fill: chartColors.axis }}
            tickFormatter={yAxisTickFormatter}
          />
          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={{
              backgroundColor: isDarkMode ? '#1a202c' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#4a5568' : '#e5e7eb'}`,
              borderRadius: '8px',
              color: isDarkMode ? '#ffffff' : '#1a1a1a',
            }}
            labelStyle={{
              color: isDarkMode ? '#a0aec0' : '#666',
            }}
          />
          <Line type="monotone" dataKey={valueKey} stroke={chartColors.line} strokeWidth={2} />
        </LineChart>
      );
    } else {
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
          <XAxis
            dataKey={xKey}
            stroke={chartColors.axis}
            fontSize={12}
            tick={{ fill: chartColors.axis }}
          />
          <YAxis
            stroke={chartColors.axis}
            fontSize={12}
            tick={{ fill: chartColors.axis }}
            tickFormatter={yAxisTickFormatter}
          />
          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={{
              backgroundColor: isDarkMode ? '#1a202c' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#4a5568' : '#e5e7eb'}`,
              borderRadius: '8px',
              color: isDarkMode ? '#ffffff' : '#1a1a1a',
            }}
            labelStyle={{
              color: isDarkMode ? '#a0aec0' : '#666',
            }}
          />
          <Bar dataKey={valueKey} fill={chartColors.bar} radius={[8, 8, 0, 0]} />
        </BarChart>
      );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <ChartCard>
        <ChartContainer style={{ height: `${height}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </ChartContainer>
      </ChartCard>
    </ThemeProvider>
  );
};

export default BarLineChart;
